import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, MoonStar } from 'lucide-react';
import { Card, Dot, SectionHeader, Select, Skeleton, Switch } from '../../../components/ui';
import { NOTIFICATION_LABELS } from '../../../types/notifications';
import type { NotificationKind } from '../../../types/notifications';
import { HOURS, formatHour, quietHoursOf, quietHoursSummary, validateQuietHours } from '../format';
import { MailStatus, usePreferences } from '../useNotifications';
import { Notice, THUMB_REACH } from './ui';

const DEFAULT_QUIET: { from: number; to: number } = { from: 21, to: 7 };

/** The line under a kind: what it is, plus when it can reach you */
const timing = (kind: NotificationKind): string | null => {
  if (kind === 'critical_alert') return 'Sent straight away, even during quiet hours';
  return NOTIFICATION_LABELS[kind].immediate ? 'Sent straight away' : null;
};

/**
 * What reaches a person by email, and when it may not. Every change saves on the
 * spot; a refusal puts the switch back and says why.
 */
export const PreferencesPanel: React.FC<{ mailStatus: MailStatus; className?: string }> = ({
  mailStatus,
  className = '',
}) => {
  const { subscriptions, loading, error, unavailable, busy, toggle, saveQuietHours } = usePreferences();
  const saved = quietHoursOf(subscriptions);
  // A half-made window (both ends on the same hour) is shown and explained, never saved
  const [draft, setDraft] = useState<{ from: number | null; to: number | null } | null>(null);
  const quiet = draft ?? saved;
  const quietOn = quiet.from !== null && quiet.to !== null;
  const quietError = quietOn ? validateQuietHours(quiet.from, quiet.to) : null;

  const setWindow = (from: number | null, to: number | null) => {
    setDraft({ from, to });
    if (from === null || to === null) {
      setDraft(null);
      saveQuietHours(null, null);
      return;
    }
    if (!validateQuietHours(from, to)) saveQuietHours(from, to);
  };

  // "Saved" appears only after a change that actually landed
  const [justSaved, setJustSaved] = useState(false);
  const wasBusy = useRef(false);
  useEffect(() => {
    if (busy) {
      wasBusy.current = true;
      return;
    }
    if (wasBusy.current && !error) {
      wasBusy.current = false;
      setJustSaved(true);
      const timer = window.setTimeout(() => setJustSaved(false), 2400);
      return () => window.clearTimeout(timer);
    }
    wasBusy.current = false;
  }, [busy, error]);

  const nothingDelivered = mailStatus === 'not-configured' || mailStatus === 'unavailable';

  if (unavailable) {
    return (
      <section className={className}>
        <SectionHeader title="What reaches you" />
        <Card className="p-3">
          <Notice tone="quiet">
            Email notifications are not available on this server yet — the backend needs to be redeployed.
          </Notice>
        </Card>
      </section>
    );
  }

  return (
    <section className={className}>
      <SectionHeader
        title="What reaches you"
        aside={
          busy ? (
            <span className="inline-flex items-center gap-1.5">
              <Dot tone="amber" /> Saving
            </span>
          ) : justSaved ? (
            <span className="inline-flex animate-fade-up items-center gap-1.5">
              <Check className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2} /> Saved
            </span>
          ) : undefined
        }
      />

      {nothingDelivered && (
        <Notice
          className="mb-3"
          tone="quiet"
          action={
            mailStatus === 'not-configured' ? (
              <Link
                to="/ai-settings"
                className="text-[12px] text-ink underline decoration-neutral-300 underline-offset-2 hover:decoration-ink"
              >
                Mail settings
              </Link>
            ) : undefined
          }
        >
          {mailStatus === 'not-configured'
            ? 'Nothing will be delivered yet: email is not set up on this server. An admin can set it up in mail settings.'
            : 'Nothing will be delivered yet: email is not available on this server.'}
        </Notice>
      )}

      <Card divided>
        {loading
          ? Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="flex items-center justify-between gap-4 px-4 py-3.5">
                <div className="min-w-0 flex-1">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="mt-2 h-3 w-56" />
                </div>
                <Skeleton className="h-6 w-10 rounded-full" />
              </div>
            ))
          : subscriptions.map((sub) => {
              const label = NOTIFICATION_LABELS[sub.kind];
              const when = timing(sub.kind);
              return (
                <div key={sub.kind} className="flex items-start justify-between gap-4 px-4 py-3.5">
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] text-ink">{label.title}</p>
                    <p className="mt-0.5 text-[12px] text-neutral-500">{label.description}</p>
                    {when && <p className="mt-0.5 text-[12px] text-neutral-400">{when}</p>}
                  </div>
                  <Switch
                    className={`mt-0.5 ${THUMB_REACH}`}
                    label={label.title}
                    checked={sub.enabled}
                    disabled={busy === sub.kind}
                    onChange={(next) => toggle(sub.kind, next)}
                  />
                </div>
              );
            })}

        {!loading && (
          <div className="px-4 py-3.5">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-1.5 text-[13px] text-ink">
                  <MoonStar className="h-4 w-4 text-neutral-400" strokeWidth={1.75} />
                  Quiet hours
                </p>
                <p className="mt-0.5 text-[12px] text-neutral-500">
                  {/* While a half-made window is on screen, the line keeps describing what is actually saved */}
                  {quietError
                    ? quietHoursSummary(saved.from, saved.to)
                    : quietOn
                      ? quietHoursSummary(quiet.from, quiet.to)
                      : quietHoursSummary(null, null)}
                </p>
              </div>
              <Switch
                className={`mt-0.5 ${THUMB_REACH}`}
                label="Quiet hours"
                checked={quietOn}
                disabled={busy === 'quiet'}
                onChange={(next) =>
                  next ? setWindow(DEFAULT_QUIET.from, DEFAULT_QUIET.to) : setWindow(null, null)
                }
              />
            </div>

            {quietOn && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <label htmlFor="quiet-from" className="text-[12px] text-neutral-500">
                  From
                </label>
                <Select
                  id="quiet-from"
                  className="w-[104px]"
                  value={String(quiet.from)}
                  disabled={busy === 'quiet'}
                  onChange={(event) => setWindow(Number(event.target.value), quiet.to)}
                >
                  {HOURS.map((hour) => (
                    <option key={hour} value={hour}>
                      {formatHour(hour)}
                    </option>
                  ))}
                </Select>
                <label htmlFor="quiet-to" className="text-[12px] text-neutral-500">
                  to
                </label>
                <Select
                  id="quiet-to"
                  className="w-[104px]"
                  value={String(quiet.to)}
                  disabled={busy === 'quiet'}
                  onChange={(event) => setWindow(quiet.from, Number(event.target.value))}
                >
                  {HOURS.map((hour) => (
                    <option key={hour} value={hour}>
                      {formatHour(hour)}
                    </option>
                  ))}
                </Select>
              </div>
            )}

            {quietError && <p className="mt-2 text-[12px] text-primary-700">{quietError}</p>}
          </div>
        )}
      </Card>

      {error && (
        <Notice className="mt-3" tone="error">
          {error}
        </Notice>
      )}
    </section>
  );
};
